import { useEffect, useRef, useState } from "react";
import VolcanoGame from "@/game/volcano";
import { preloadAssets } from "@/game/assets";
import { AudioManager } from "@/game/audio";
import { useLang } from "@/i18n";
import { BonusLeaderboard } from "@/components/BonusLeaderboard";
import { Volume2, VolumeX, Settings, ChevronLeft, ChevronRight } from "lucide-react";
import BonusPauseMenu from "@/components/BonusPauseMenu";
import { stopTitleAudio } from "@/lib/audioBus";

const BONUS_PER_COIN = 75;
const MAX_ATTEMPTS = 2;

export default function BonusRound({ character, onContinue, onExit }) {
  const { t } = useLang();
  const canvasRef = useRef(null);
  const gameRef = useRef(null);
  const audioRef = useRef(null);
  const attemptRef = useRef(1);   // current attempt (1 or 2)
  const best1Ref = useRef(0);     // coins from attempt 1

  const [phase, setPhase] = useState("loading"); // loading | countdown | running | attemptResult | final
  const [attempt, setAttempt] = useState(1);
  const [countNum, setCountNum] = useState(3);
  const [progress, setProgress] = useState({ collected: 0, total: 100, pct: 0 });
  const [attemptData, setAttemptData] = useState(null); // result of attempt 1
  const [result, setResult] = useState(null);           // final (best) result
  const [muted, setMuted] = useState(false);
  const [paused, setPaused] = useState(false);

  const openPause = () => { const g = gameRef.current; if (g) g.paused = true; setPaused(true); };
  const resumePlay = () => { gameRef.current?.begin(); setPaused(false); };

  // Silence the landing-page song the moment the bonus round mounts (belt-and-
  // suspenders alongside the scene effect, so its music never bleeds in).
  useEffect(() => { stopTitleAudio(); }, []);

  // ---- Create the engine once ----
  useEffect(() => {
    let game;
    let cancelled = false;
    preloadAssets(["volcano_slope", "volcano_crater", "volcano_town", character?.id]).then((assets) => {
      if (cancelled) return;
      const audio = new AudioManager();
      audioRef.current = audio;
      game = new VolcanoGame(canvasRef.current, {
        character,
        assets,
        audio,
        callbacks: {
          onProgress: setProgress,
          onComplete: ({ collected, total }) => {
            if (attemptRef.current >= MAX_ATTEMPTS) {
              const best = Math.max(best1Ref.current, collected);
              setResult({ collected: best, total, bonus: best * BONUS_PER_COIN });
              setPhase("final");
            } else {
              best1Ref.current = collected;
              setAttemptData({ collected, total, bonus: collected * BONUS_PER_COIN });
              setPhase("attemptResult");
            }
          },
        },
      });
      gameRef.current = game;
      game.start();         // loop runs but stays paused until the countdown ends
      setPhase("countdown");
    });
    return () => {
      cancelled = true;
      game?.destroy();
      audioRef.current?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- 3-2-1 countdown before each attempt ----
  useEffect(() => {
    if (phase !== "countdown") return;
    setCountNum(3);
    const tm = [];
    tm.push(setTimeout(() => setCountNum(2), 750));
    tm.push(setTimeout(() => setCountNum(1), 1500));
    tm.push(setTimeout(() => setCountNum("GO"), 2250));
    tm.push(setTimeout(() => {
      gameRef.current?.begin();
      setPhase("running");
    }, 3000));
    return () => tm.forEach(clearTimeout);
  }, [phase, attempt]);

  const startAttempt2 = () => {
    gameRef.current?.reset();
    attemptRef.current = 2;
    setAttempt(2);
    setProgress({ collected: 0, total: 100, pct: 0 });
    setAttemptData(null);
    setPhase("countdown");
  };

  const toggleMute = () => {
    const m = !muted;
    setMuted(m);
    audioRef.current?.setMuted(m);
  };

  const steerRef = useRef(false);
  const dirRef = useRef(null);
  const [dir, setDir] = useState(null);
  const applyDir = (clientX, target) => {
    const rect = target.getBoundingClientRect();
    const left = clientX < rect.left + rect.width / 2;
    gameRef.current?.setInput("left", left);
    gameRef.current?.setInput("right", !left);
    const d = left ? "left" : "right";
    if (dirRef.current !== d) { dirRef.current = d; setDir(d); }
  };
  const clearDir = () => {
    steerRef.current = false; dirRef.current = null; setDir(null);
    gameRef.current?.setInput("left", false); gameRef.current?.setInput("right", false);
  };
  const steer = {
    onPointerDown: (e) => { e.preventDefault(); e.currentTarget.setPointerCapture?.(e.pointerId); steerRef.current = true; applyDir(e.clientX, e.currentTarget); },
    onPointerMove: (e) => { if (steerRef.current) applyDir(e.clientX, e.currentTarget); },
    onPointerUp: clearDir,
    onPointerCancel: clearDir,
    onContextMenu: (e) => e.preventDefault(),
    draggable: false,
    style: { touchAction: "none", userSelect: "none", WebkitUserSelect: "none", WebkitTouchCallout: "none", WebkitTapHighlightColor: "transparent" },
  };

  const perfect = result && result.collected >= result.total;

  return (
    <div className="relative w-full h-full bg-black overflow-hidden" style={{ touchAction: "none" }} data-testid="bonus-round">
      <canvas id="bonus-canvas" ref={canvasRef} width={960} height={540} />

      {/* Top HUD */}
      <div className="absolute top-0 left-0 w-full p-3 md:p-4 flex justify-between items-start z-40 pointer-events-none font-pixel pixel-stroke text-white">
        <div className="flex flex-col gap-1 pointer-events-none">
          <div className="text-[10px] md:text-sm text-[#FFEB3B]">{t("bonusTitle")}</div>
          <div data-testid="bonus-count" className="text-base md:text-2xl text-white">
            C$ {progress.collected} <span className="text-white/60 text-xs md:text-base">/ {progress.total}</span>
          </div>
          <div data-testid="bonus-attempt" className="text-[8px] md:text-[10px] text-[#FF5722]">{t("attemptOf")(attempt)}</div>
        </div>
        <div className="flex flex-col items-center gap-1 pointer-events-none">
          <div className="text-[8px] md:text-[10px] text-white/80">{t("bonusSteer")}</div>
          <div className="mt-1 w-40 md:w-56 h-3 rounded-full bg-black/40 overflow-hidden">
            <div className="h-full rounded-full bg-[#ff5722] transition-all duration-150" style={{ width: `${progress.pct}%` }} />
          </div>
        </div>
        <div className="pointer-events-auto flex gap-2">
          {phase === "running" && (
            <button data-testid="bonus-settings-btn" onClick={openPause} className="pixel-btn bg-black/55 backdrop-blur text-white p-2.5">
              <Settings size={16} strokeWidth={2.5} />
            </button>
          )}
          <button data-testid="bonus-mute-btn" onClick={toggleMute} className="pixel-btn bg-black/55 backdrop-blur text-white p-2.5">
            {muted ? <VolumeX size={16} strokeWidth={2.5} /> : <Volume2 size={16} strokeWidth={2.5} />}
          </button>
        </div>
      </div>

      <BonusPauseMenu open={paused} onResume={resumePlay} onExit={() => onExit && onExit()} muted={muted} onToggleSound={toggleMute} />

      {/* Touch steering — slide/press anywhere on the lower area; left half = left,
          right half = right. Buttons are fixed visual indicators only. */}
      {phase === "running" && (
        <div {...steer} className="touch-only absolute inset-x-0 bottom-0 z-30 h-[64%] flex items-center justify-between px-4 select-none" data-testid="bonus-steer-zone">
          <div className={`pixel-btn text-white w-40 h-40 sm:w-48 sm:h-48 flex items-center justify-center opacity-90 pointer-events-none transition-colors ${dir === "left" ? "bg-[#FF5722]" : "bg-black/50"}`} data-testid="bonus-left"><ChevronLeft size={72} strokeWidth={3} /></div>
          <div className={`pixel-btn text-white w-40 h-40 sm:w-48 sm:h-48 flex items-center justify-center opacity-90 pointer-events-none transition-colors ${dir === "right" ? "bg-[#FF5722]" : "bg-black/50"}`} data-testid="bonus-right"><ChevronRight size={72} strokeWidth={3} /></div>
        </div>
      )}

      {/* Loading */}
      {phase === "loading" && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black">
          <p className="font-pixel text-[#FFEB3B] text-sm blink">{t("loadingGame")}</p>
        </div>
      )}

      {/* 3-2-1 countdown overlay */}
      {phase === "countdown" && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/55" data-testid="bonus-countdown">
          <div className="font-pixel text-[10px] md:text-sm text-[#FFEB3B] pixel-stroke mb-4">{t("attemptOf")(attempt)}</div>
          <div
            key={String(countNum)}
            className="font-pixel pixel-stroke pop-in text-7xl md:text-9xl"
            style={{ color: countNum === "GO" ? "#4CAF50" : "#FF5722" }}
            data-testid="bonus-countdown-num"
          >
            {countNum === "GO" ? t("bonusCountGo") : countNum}
          </div>
        </div>
      )}

      {/* Attempt 1 result -> offer the 2nd try */}
      {phase === "attemptResult" && attemptData && (
        <div className="absolute inset-0 z-[60] flex items-center justify-center bg-black/85 p-4 scanlines">
          <div className="pixel-card bg-[#141414] p-6 md:p-10 w-full max-w-md text-center pop-in" data-testid="bonus-attempt-modal">
            <h2 className="font-pixel text-xl md:text-3xl pixel-stroke text-[#FFEB3B]">{t("attemptDone")(1)}</h2>
            <div className="my-6 py-4 border-y-2 border-white/15">
              <p className="font-pixel text-[10px] text-[#FFEB3B]">{t("cordobas")}</p>
              <p className="font-pixel text-4xl md:text-5xl text-white mt-2" data-testid="bonus-attempt-count">
                {attemptData.collected}<span className="text-white/50 text-2xl"> / {attemptData.total}</span>
              </p>
              <p className="font-vt text-lg text-[#4CAF50] mt-3">{t("bestSoFar")}: {attemptData.collected}</p>
            </div>
            <p className="font-vt text-xl text-white mb-4">{t("ready2nd")}</p>
            <button data-testid="bonus-try2-btn" onClick={startAttempt2} className="pixel-btn bg-[#FF5722] text-white px-6 py-3 text-xs w-full">
              {t("start2nd")}
            </button>
          </div>
        </div>
      )}

      {/* Final (best of two) result */}
      {phase === "final" && result && (
        <div className="absolute inset-0 z-[60] flex items-center justify-center bg-black/85 p-4 scanlines">
          <div className="pixel-card bg-[#141414] p-6 md:p-10 w-full max-w-md text-center pop-in" data-testid="bonus-result-modal">
            <h2 className="font-pixel text-2xl md:text-4xl pixel-stroke text-[#FF5722]">{perfect ? t("bonusPerfect") : t("bonusResultTitle")}</h2>
            <p className="font-vt text-2xl text-white mt-3">{t("bonusTitle")}</p>
            <div className="my-6 py-4 border-y-2 border-white/15">
              <p className="font-pixel text-[10px] text-[#FFEB3B]">{t("bestSoFar")}</p>
              <p className="font-pixel text-4xl md:text-5xl text-white mt-2" data-testid="bonus-final-count">{result.collected}<span className="text-white/50 text-2xl"> / {result.total}</span></p>
              <p className="font-vt text-xl text-[#4CAF50] mt-3">{t("bonusBonus")}: +{result.bonus}</p>
            </div>
            <BonusLeaderboard mode="volcano" levelIndex={8} levelName="Volcano Boarding" character={character} score={result.collected} />
            <button data-testid="bonus-continue-btn" onClick={() => onContinue(result)} className="pixel-btn bg-[#4CAF50] text-white px-6 py-3 text-xs w-full">
              {t("continueBtn")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
